/**
 * Hero video waits until the visitor actively chooses cookie settings.
 * With ?preview_privacy_banner=1, existing consent is ignored — wait for a new choice.
 */
(function () {
  const VIDEO_SELECTOR = '.hero__media-wrapper video.hero__video';

  function getVideos() {
    return Array.from(document.querySelectorAll(VIDEO_SELECTOR));
  }

  function pauseVideos() {
    getVideos().forEach((video) => {
      video.autoplay = false;
      video.removeAttribute('autoplay');
      if (!video.paused) video.pause();
      try {
        video.currentTime = 0;
      } catch (error) {
        /* ignore */
      }
    });
  }

  function playVideos() {
    getVideos().forEach((video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.autoplay = true;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });
  }

  function isPrivacyBannerPreview() {
    try {
      return new URLSearchParams(window.location.search).get('preview_privacy_banner') === '1';
    } catch (error) {
      return false;
    }
  }

  function isBannerInDom() {
    return Boolean(
      document.querySelector(
        '#shopify-pc__banner, .shopify-pc__banner, .shopify-pc__banner__dialog, #shopify-pc__banner__dialog'
      )
    );
  }

  function readConsent(privacy) {
    if (!privacy || typeof privacy.currentVisitorConsent !== 'function') return null;
    try {
      return privacy.currentVisitorConsent() || {};
    } catch (error) {
      return null;
    }
  }

  function hasConsentDecision(privacy) {
    const consent = readConsent(privacy);
    if (!consent) return false;
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some((key) => {
      const value = consent[key];
      return value === true || value === false || value === 'yes' || value === 'no';
    });
  }

  function shouldShowBanner(privacy) {
    if (!privacy || typeof privacy.shouldShowBanner !== 'function') return null;
    try {
      return Boolean(privacy.shouldShowBanner());
    } catch (error) {
      return null;
    }
  }

  function loadPrivacyApi(callback) {
    if (window.Shopify?.customerPrivacy) {
      callback(window.Shopify.customerPrivacy);
      return;
    }

    if (typeof window.Shopify?.loadFeatures !== 'function') {
      callback(null);
      return;
    }

    let done = false;
    const finish = (api) => {
      if (done) return;
      done = true;
      callback(api);
    };

    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        finish(error ? null : window.Shopify.customerPrivacy || null);
      });
    } catch (error) {
      finish(null);
      return;
    }

    window.setTimeout(() => finish(window.Shopify?.customerPrivacy || null), 1500);
  }

  function waitForNewConsentChoice(onReady) {
    let settled = false;
    let holdId = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (holdId) window.clearInterval(holdId);
      onReady();
    };

    pauseVideos();
    holdId = window.setInterval(pauseVideos, 120);

    // Only a NEW consent event after this page load unlocks playback
    document.addEventListener('visitorConsentCollected', finish, { once: true });

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest?.(
          [
            '.shopify-pc__banner__btn-accept',
            '.shopify-pc__banner__btn-decline',
            '.shopify-pc__banner__btn-manage-prefs',
            '#shopify-pc__prefs__header-accept',
            '#shopify-pc__prefs__header-decline',
            '#shopify-pc__prefs__header-save',
            '#shopify-pc__banner button',
            '.shopify-pc__banner button',
            '.shopify-pc__prefs button',
          ].join(', ')
        );
        if (!target) return;

        // Preferences may open a second dialog — only finish on accept/decline/save
        const isDecisionButton = target.matches(
          [
            '.shopify-pc__banner__btn-accept',
            '.shopify-pc__banner__btn-decline',
            '#shopify-pc__prefs__header-accept',
            '#shopify-pc__prefs__header-decline',
            '#shopify-pc__prefs__header-save',
          ].join(', ')
        );

        if (!isDecisionButton) return;

        window.setTimeout(finish, 100);
      },
      true
    );
  }

  function mustWaitForConsent(privacy) {
    if (isPrivacyBannerPreview()) return true;
    // Region requires a banner and visitor has not chosen yet
    if (shouldShowBanner(privacy) === true && !hasConsentDecision(privacy)) return true;
    return false;
  }

  function start() {
    if (!getVideos().length) return;

    if (window.Shopify?.designMode) {
      playVideos();
      return;
    }

    pauseVideos();

    // Forced banner preview: never use saved cookies — wait for a choice on this page
    if (isPrivacyBannerPreview()) {
      waitForNewConsentChoice(playVideos);
      return;
    }

    loadPrivacyApi((privacy) => {
      if (mustWaitForConsent(privacy)) {
        waitForNewConsentChoice(playVideos);
        return;
      }

      // Banner may inject a moment later when required
      if (shouldShowBanner(privacy) === true) {
        let tries = 0;
        const pollId = window.setInterval(() => {
          tries += 1;
          if (isBannerInDom() && !hasConsentDecision(window.Shopify?.customerPrivacy)) {
            window.clearInterval(pollId);
            waitForNewConsentChoice(playVideos);
            return;
          }
          if (tries >= 25) {
            window.clearInterval(pollId);
            if (mustWaitForConsent(window.Shopify?.customerPrivacy)) {
              waitForNewConsentChoice(playVideos);
            } else if (hasConsentDecision(window.Shopify?.customerPrivacy) || shouldShowBanner(window.Shopify?.customerPrivacy) === false) {
              playVideos();
            } else {
              waitForNewConsentChoice(playVideos);
            }
          }
        }, 100);
        return;
      }

      playVideos();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
